import React, { useEffect, useState, useRef, useCallback } from 'react'
import { Music, Calendar, Tags, Loader2, Filter, HelpCircle } from 'lucide-react'
import * as d3 from 'd3'
import { FloatingMusicPlayer } from '@/components/FloatingMusicPlayer'
import { AppTutorial, TutorialStep } from '@/components/AppTutorial'
import { getVisualizerAssetUrl } from '@/config/visualizerAssets'
import { getTags } from '@/services/tagService'
import { getTagColor, TagData } from '@/types/tags'

interface Track {
  hash_320: string
  singer_name: string
  songname: string
  publish_year: string
  genre_top1: string
  img_fname: string
}

interface MixPair {
  index: number
  track_prev: string
  track_next: string
}

interface VisualizationData {
  publish_years: string[]
  genres: string[]
  track_list: Track[]
  mix_pairs: MixPair[]
}

interface MixinfoTrack {
  hashName: string
  hash: string
  song: string
  artist: string
  bpm: number
}

interface MixinfoPair {
  index: number
  track_prev: MixinfoTrack
  track_next: MixinfoTrack
  mergedSyncOutOFP?: string
  mergedSyncInOFP?: string
}

interface Mixinfo {
  sequence_id: string
  playlist_name: string
  mix_pairs: MixinfoPair[]
}

// 纵轴流派顺序：相近风格相邻排列
const GENRE_ORDER = [
  // 亚洲流行
  'J-pop',
  'K-pop',
  'City Pop',
  // 欧美流行
  'Europop',
  'Dance-pop',
  'Synth-pop',
  'Pop Rock',
  // House / 电子舞曲
  'House',
  'Deep House',
  'Euro House',
  'Tropical House',
  'Nu-Disco',
  'Bassline',
  'Hands Up',
  'Electro',
  'Big Beat',
  // 嘻哈 / 说唱
  'G-Funk',
  'Gangsta',
  'Cloud Rap',
  'Trap',
  // R&B / Soul / Jazz
  'Smooth Jazz',
  'Neo Soul',
  'RnB/Swing',
  'Contemporary R&B',
]

const sortGenresBySimilarity = (genres: string[]): string[] => {
  const orderMap = new Map(GENRE_ORDER.map((genre, index) => [genre, index]))
  return [...genres].sort((a, b) => {
    const indexA = orderMap.get(a) ?? Number.MAX_SAFE_INTEGER
    const indexB = orderMap.get(b) ?? Number.MAX_SAFE_INTEGER
    return indexA - indexB
  })
}

export const MusicVisualizerPage: React.FC = () => {
  const [data, setData] = useState<VisualizationData | null>(null)
  const [mixinfo, setMixinfo] = useState<Mixinfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedPoint, setSelectedPoint] = useState<Track | null>(null)
  const [isAltKeyPressed, setIsAltKeyPressed] = useState(false)
  const [isPlayerOpen, setIsPlayerOpen] = useState(false)
  const [selectedPair, setSelectedPair] = useState<{ prev: Track; next: Track; mixPairIndex?: number } | null>(null)
  
  // Tag 相关状态
  const [tagData, setTagData] = useState<TagData | null>(null)
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  
  // 渲染状态
  const [isRendering, setIsRendering] = useState(false)
  const hasRenderedOnce = useRef(false) // 追踪是否已经完成过首次渲染
  
  const svgRef = useRef<SVGSVGElement>(null)
  const hoverTimeoutRef = useRef<number | null>(null)
  const prevListContainerRef = useRef<HTMLDivElement>(null)
  const nextListContainerRef = useRef<HTMLDivElement>(null)
  
  // Tutorial state
  const [isTutorialOpen, setIsTutorialOpen] = useState(false)

  // 加载tags
  useEffect(() => {
    const loadTags = async () => {
      const tags = await getTags()
      setTagData(tags)
    }
    loadTags()
  }, [])

  // 刷新tags
  const refreshTags = async () => {
    const tags = await getTags()
    setTagData(tags)
  }

  // 获取拥有选中tags的mixpair索引列表
  const getFilteredMixPairIndices = useCallback(() => {
    if (!tagData || selectedTags.length === 0) return []
    
    const indices: number[] = []
    Object.entries(tagData.mixPairTags).forEach(([indexStr, tags]) => {
      const hasAllSelectedTags = selectedTags.every(selectedTag => tags.includes(selectedTag))
      if (hasAllSelectedTags) {
        indices.push(parseInt(indexStr))
      }
    })
    return indices
  }, [tagData, selectedTags])

  // 切换tag选择
  const toggleTagSelection = (tag: string) => {
    setSelectedTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    )
  }

  // 根据mixpair索引查找tracks
  const findTracksByMixPairIndex = useCallback((index: number) => {
    if (!data?.mix_pairs) return null
    const pair = data.mix_pairs.find(p => p.index === index)
    if (!pair) return null
    
    const prevTrack = data.track_list.find(t => t.hash_320 === pair.track_prev)
    const nextTrack = data.track_list.find(t => t.hash_320 === pair.track_next)
    
    if (prevTrack && nextTrack) {
      return { prev: prevTrack, next: nextTrack, mixPairIndex: index }
    }
    return null
  }, [data])

  // 通过tag打开播放器
  const openPlayerByTag = (mixPairIndex: number) => {
    const tracks = findTracksByMixPairIndex(mixPairIndex)
    if (tracks) {
      setSelectedPair(tracks)
      setIsPlayerOpen(true)
    }
  }

  // Function to scroll to a specific track in lists (with exclusion option)
  const scrollToTrackInList = useCallback((trackHash: string, excludeList?: 'prev' | 'next') => {
    if (!data) return
    
    // Check if track is in prev list
    const prevTracks = data.mix_pairs?.map(pair => pair.track_prev) || []
    const isInPrevList = prevTracks.includes(trackHash)
    
    // Check if track is in next list
    const nextTracks = data.mix_pairs?.map(pair => pair.track_next) || []
    const isInNextList = nextTracks.includes(trackHash)
    
    // Scroll in prev list if track is there and not excluded
    if (isInPrevList && excludeList !== 'prev' && prevListContainerRef.current) {
      const targetElement = prevListContainerRef.current.querySelector(`[data-track-hash="${trackHash}"]`)
      if (targetElement) {
        const listContainer = prevListContainerRef.current
        const containerHeight = listContainer.clientHeight
        
        const targetRect = targetElement.getBoundingClientRect()
        const containerRect = listContainer.getBoundingClientRect()
        
        const targetOffsetTop = targetRect.top - containerRect.top + listContainer.scrollTop
        const targetHeight = targetRect.height
        
        const scrollTop = targetOffsetTop - (containerHeight / 2) + (targetHeight / 2)
        
        listContainer.scrollTo({
          top: Math.max(0, scrollTop),
          behavior: 'smooth'
        })
      }
    }
    
    // Scroll in next list if track is there and not excluded
    if (isInNextList && excludeList !== 'next' && nextListContainerRef.current) {
      const targetElement = nextListContainerRef.current.querySelector(`[data-track-hash="${trackHash}"]`)
      if (targetElement) {
        const listContainer = nextListContainerRef.current
        const containerHeight = listContainer.clientHeight
        
        const targetRect = targetElement.getBoundingClientRect()
        const containerRect = listContainer.getBoundingClientRect()
        
        const targetOffsetTop = targetRect.top - containerRect.top + listContainer.scrollTop
        const targetHeight = targetRect.height
        
        // Adjust for alignment with prev list (add approximately 1.3 item heights)
        const itemHeightAdjustment = 92 // Approximate height of 1.3 items including padding and spacing
        const scrollTop = targetOffsetTop - (containerHeight / 2) + (targetHeight / 2) + itemHeightAdjustment
        
        listContainer.scrollTo({
          top: Math.max(0, scrollTop),
          behavior: 'smooth'
        })
      }
    }
  }, [data])

  // Function to find connected tracks (only next tracks where current track is prev)
  const getConnectedTracks = useCallback((trackHash: string) => {
    if (!data?.mix_pairs) return []
    
    const connected: string[] = []
    data.mix_pairs.forEach(pair => {
      if (pair.track_prev === trackHash) {
        connected.push(pair.track_next)
      }
    })
    return connected
  }, [data?.mix_pairs])

  // Function to find reverse connected tracks (prev tracks where current track is next)
  const getReverseConnectedTracks = useCallback((trackHash: string) => {
    if (!data?.mix_pairs) return []
    
    const connected: string[] = []
    data.mix_pairs.forEach(pair => {
      if (pair.track_next === trackHash) {
        connected.push(pair.track_prev)
      }
    })
    return connected
  }, [data?.mix_pairs])

  // Function to clear all highlights
  const clearHighlights = useCallback(() => {
    if (!svgRef.current) return
    
    const svg = d3.select(svgRef.current)
    svg.selectAll(".album-cover").classed("highlighted", false).classed("connected", false)
    svg.selectAll(".mix-connection").classed("highlighted", false)
  }, [])

  // Function to highlight connected items and lines
  const highlightConnections = useCallback((trackHash: string, useReverseMode: boolean = false) => {
    if (!svgRef.current) return
    
    const svg = d3.select(svgRef.current)
    const connectedTracks = useReverseMode 
      ? getReverseConnectedTracks(trackHash)
      : getConnectedTracks(trackHash)
    
    // Reset all items to normal state
    clearHighlights()
    
    // First, highlight and raise connected lines
    const highlightedConnections = svg.selectAll(".mix-connection")
      .classed("highlighted", function() {
        if (useReverseMode) {
          // Highlight lines where current track is next
          const nextTrack = d3.select(this).attr("data-next-track")
          return nextTrack === trackHash
        } else {
          // Highlight lines where current track is prev
          const prevTrack = d3.select(this).attr("data-prev-track")
          return prevTrack === trackHash
        }
      })
    
    // Bring highlighted connections above non-highlighted items
    highlightedConnections.filter(".highlighted").raise()
    
    // Then, highlight and bring to front the main item (above connections)
    const mainItem = svg.selectAll(`.album-cover[data-track-hash="${trackHash}"]`)
    mainItem.classed("highlighted", true)
    mainItem.raise() // Bring to front, above connections
    
    // Finally, highlight and bring to front connected items (above connections)
    connectedTracks.forEach(connectedHash => {
      const connectedItem = svg.selectAll(`.album-cover[data-track-hash="${connectedHash}"]`)
      connectedItem.classed("connected", true)
      connectedItem.raise() // Bring to front, above connections
    })
  }, [getConnectedTracks, getReverseConnectedTracks, clearHighlights])

  useEffect(() => {
    const fetchData = async () => {
      try {
        // 加载 visualization.json
        const vizResponse = await fetch(getVisualizerAssetUrl('visualization.json'))
        if (!vizResponse.ok) {
          throw new Error('Failed to fetch visualization data')
        }
        const jsonData: VisualizationData = await vizResponse.json()
        setData(jsonData)
        
        // 加载 mixinfo.json
        try {
          const mixinfoResponse = await fetch(getVisualizerAssetUrl('mixes/mixinfo.json'))
          if (mixinfoResponse.ok) {
            const mixinfoData: Mixinfo = await mixinfoResponse.json()
            setMixinfo(mixinfoData)
            console.log('✅ Mixinfo loaded successfully')
          } else {
            console.warn('⚠️ Mixinfo not found, audio URLs will not be available')
          }
        } catch (mixinfoErr) {
          console.warn('⚠️ Failed to load mixinfo:', mixinfoErr)
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  useEffect(() => {
    if (!data || !svgRef.current || !tagData) return

    // 只在首次渲染时显示加载状态
    if (!hasRenderedOnce.current) {
      setIsRendering(true)
    }

    const svg = d3.select(svgRef.current)
    svg.selectAll("*").remove() // Clear previous render

    const margin = { top: 40, right: 40, bottom: 80, left: 120 }
    const width = 1800 - margin.left - margin.right  // 增加宽度到1800
    const height = 1000 - margin.top - margin.bottom  // 增加高度到1000

    // Create scales
    const xScale = d3.scaleLinear()
      .domain([1980, 2030])
      .range([0, width])

    const sortedGenres = sortGenresBySimilarity(data.genres)

    const yScale = d3.scalePoint()
      .domain(sortedGenres)
      .range([20, height - 60])  // 顶部留20px，底部留60px空间
      .padding(0.1)

    // 获取应该高亮的mixpair索引
    const highlightedMixPairIndices = getFilteredMixPairIndices()

    // Add definitions for styles
    const defs = svg.append("defs")

    // Add CSS styles for highlighting and theme support
    defs.append("style").text(`
      .axis-text {
        fill: rgb(75 85 99);
      }
      .axis-label {
        fill: rgb(55 65 81);
      }
      .axis-line {
        stroke: rgb(75 85 99);
      }
      .grid-line-vertical, .grid-line-horizontal {
        stroke: rgb(156 163 175);
      }

      
      @media (prefers-color-scheme: dark) {
        .axis-text {
          fill: rgb(156 163 175) !important;
        }
        .axis-label {
          fill: rgb(209 213 219) !important;
        }
        .axis-line {
          stroke: rgb(156 163 175) !important;
        }
        .grid-line-vertical, .grid-line-horizontal {
          stroke: rgb(75 85 99) !important;
        }
      }
      
      .album-cover.highlighted image {
        transform: scale(1.33) !important;
        filter: drop-shadow(0 0 4px rgba(59, 130, 246, 0.4)) brightness(1.05) !important;
        transition: all 0.2s ease !important;
      }
      .album-cover.highlighted rect:not(.highlight-border):not(.connected-border):not(.selection-border) {
        transform: scale(1.33) !important;
        filter: drop-shadow(0 0 4px rgba(59, 130, 246, 0.4)) brightness(1.05) !important;
        transition: all 0.2s ease !important;
      }
      .album-cover.highlighted .highlight-border {
        display: block !important;
      }
      .album-cover.connected image {
        transform: scale(1.33) !important;
        filter: drop-shadow(0 0 3px rgba(34, 197, 94, 0.4)) brightness(1.03) !important;
        transition: all 0.2s ease !important;
      }
      .album-cover.connected rect:not(.highlight-border):not(.connected-border):not(.selection-border) {
        transform: scale(1.33) !important;
        filter: drop-shadow(0 0 3px rgba(34, 197, 94, 0.4)) brightness(1.03) !important;
        transition: all 0.2s ease !important;
      }
      .album-cover.connected .connected-border {
        display: block !important;
      }
      .mix-connection.highlighted {
        stroke-width: 4px !important;
        stroke-opacity: 0.9 !important;
        stroke-dasharray: none !important;
        filter: drop-shadow(0 0 2px currentColor);
      }
      .mix-connection.tag-filtered {
        stroke-width: 3px !important;
        stroke-opacity: 0.8 !important;
        stroke-dasharray: none !important;
        filter: drop-shadow(0 0 3px rgba(255, 193, 7, 0.6));
      }
      .album-cover.tag-filtered image {
        transform: scale(1.2) !important;
        filter: drop-shadow(0 0 4px rgba(255, 193, 7, 0.5)) brightness(1.1) !important;
      }
      .album-cover.tag-filtered rect:not(.highlight-border):not(.connected-border):not(.selection-border):not(.tag-filter-border) {
        transform: scale(1.2) !important;
        filter: drop-shadow(0 0 4px rgba(255, 193, 7, 0.5)) brightness(1.1) !important;
      }
      .album-cover.tag-filtered .tag-filter-border {
        display: block !important;
      }

    `)

    // Create main group
    const g = svg.append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`)

    // Add background grid
    const xTicks = d3.range(1980, 2031, 10)
    const yTicks = sortedGenres

    // Vertical grid lines
    g.selectAll(".grid-line-vertical")
      .data(xTicks)
      .enter()
      .append("line")
      .attr("class", "grid-line-vertical")
      .attr("x1", d => xScale(d))
      .attr("x2", d => xScale(d))
      .attr("y1", 20)  // 与Y轴range顶部对齐
      .attr("y2", height - 60)  // 与Y轴range底部对齐
      .attr("stroke-width", 0.5)
      .attr("opacity", 0.15)

    // Horizontal grid lines
    g.selectAll(".grid-line-horizontal")
      .data(yTicks)
      .enter()
      .append("line")
      .attr("class", "grid-line-horizontal")
      .attr("x1", 0)
      .attr("x2", width)
      .attr("y1", d => yScale(d) || 0)
      .attr("y2", d => yScale(d) || 0)
      .attr("stroke-width", 0.5)
      .attr("opacity", 0.15)

    // Create axes
    const xAxis = d3.axisBottom(xScale)
      .tickValues([1980, 1990, 2000, 2010, 2020, 2030])
      .tickFormat(d => d.toString())

    const yAxis = d3.axisLeft(yScale)

    // Add X axis
    const xAxisGroup = g.append("g")
      .attr("transform", `translate(0,${height})`)
      .call(xAxis)
    
    xAxisGroup.selectAll("text")
      .attr("class", "axis-text")
      .style("font-size", "12px")
    
    xAxisGroup.selectAll("path, line")
      .attr("class", "axis-line")

    // Add Y axis
    const yAxisGroup = g.append("g")
      .call(yAxis)
    
    yAxisGroup.selectAll("text")
      .attr("class", "axis-text")
      .style("font-size", "12px")
    
    yAxisGroup.selectAll("path, line")
      .attr("class", "axis-line")

    // Add axis labels
    g.append("text")
      .attr("transform", `translate(${width / 2}, ${height + 50})`)
      .attr("class", "axis-label")
      .style("text-anchor", "middle")
      .style("font-size", "14px")
      .style("font-weight", "500")
      .text("Year")

    g.append("text")
      .attr("transform", "rotate(-90)")
      .attr("y", -80)
      .attr("x", -height / 2)
      .attr("class", "axis-label")
      .style("text-anchor", "middle")
      .style("font-size", "14px")
      .style("font-weight", "500")
      .text("Genre/Style")

    // Helper function to generate stable random offsets based on track hash
    const getOffset = (hash: string, axis: 'x' | 'y') => {
      const seed = hash.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
      const multiplier = axis === 'x' ? 1.5 : 2.5 // Different multipliers for x and y
      const random = Math.sin(seed * multiplier) * 10000
      const offset = (random - Math.floor(random)) * (axis === 'x' ? 40 : 70) - (axis === 'x' ? 20 : 35) // x: -20到+20, y: -35到+35
      return offset
    }

    // Helper function to get track position
    const getTrackPosition = (track: Track) => {
      const baseX = xScale(parseInt(track.publish_year))
      const baseY = yScale(track.genre_top1) || 0
      const offsetX = getOffset(track.hash_320, 'x')
      const offsetY = getOffset(track.hash_320, 'y')
      return {
        x: baseX + offsetX,
        y: baseY + offsetY
      }
    }

    // 碰撞检测和位置调整
    const adjustPositionsForCollision = (tracks: Track[]) => {
      const imageSize = 36
      const padding = 4 // 封面之间的最小间距
      const minDistance = imageSize + padding
      
      // 为每个track创建位置数据
      const positions = tracks.map(track => {
        const initialPos = getTrackPosition(track)
        return {
          track,
          x: initialPos.x,
          y: initialPos.y,
          originalX: initialPos.x,
          originalY: initialPos.y
        }
      })

      // 使用力导向模拟进行碰撞检测和调整
      const simulation = d3.forceSimulation(positions as any)
        .force('collide', d3.forceCollide().radius(minDistance / 2).strength(0.8))
        .force('x', d3.forceX((d: any) => d.originalX).strength(0.3))
        .force('y', d3.forceY((d: any) => d.originalY).strength(0.3))
        .stop()

      // 运行模拟直到稳定
      const numIterations = 100
      for (let i = 0; i < numIterations; i++) {
        simulation.tick()
      }

      // 返回调整后的位置
      return new Map(positions.map(p => [p.track.hash_320, { x: p.x, y: p.y }]))
    }

    // 计算并应用碰撞调整
    const adjustedPositions = adjustPositionsForCollision(data.track_list)

    // Draw mix pair connections
    if (data.mix_pairs && data.mix_pairs.length > 0) {
      const connectionColors = ['#8b5cf6', '#10b981'] // Purple and green alternating colors
      
      data.mix_pairs.forEach((pair, index) => {
        const prevTrack = data.track_list.find(track => track.hash_320 === pair.track_prev)
        const nextTrack = data.track_list.find(track => track.hash_320 === pair.track_next)
        
        if (prevTrack && nextTrack) {
          // 使用调整后的位置
          const startPos = adjustedPositions.get(prevTrack.hash_320) || getTrackPosition(prevTrack)
          const endPos = adjustedPositions.get(nextTrack.hash_320) || getTrackPosition(nextTrack)
          const color = connectionColors[index % connectionColors.length]
          
          // 检查是否应该高亮（tag筛选）
          const isTagFiltered = highlightedMixPairIndices.includes(pair.index)
          
          // Draw connection line
          g.append("line")
            .attr("class", `mix-connection ${isTagFiltered ? 'tag-filtered' : ''}`)
            .attr("data-connection-id", `${pair.track_prev}-${pair.track_next}`)
            .attr("data-prev-track", pair.track_prev)
            .attr("data-next-track", pair.track_next)
            .attr("x1", startPos.x)
            .attr("y1", startPos.y)
            .attr("x2", endPos.x)
            .attr("y2", endPos.y)
            .attr("stroke", isTagFiltered ? '#ffc107' : color)
            .attr("stroke-width", isTagFiltered ? 3 : 2)
            .attr("stroke-opacity", isTagFiltered ? 0.8 : 0.25)
            .attr("stroke-dasharray", isTagFiltered ? "none" : "5,5")
            .style("cursor", "pointer")
            .on("click", function(event) {
              event.stopPropagation()
              setSelectedPair({ 
                prev: prevTrack, 
                next: nextTrack,
                mixPairIndex: pair.index
              })
              setIsPlayerOpen(true)
            })
            .on("mouseover", function() {
              d3.select(this)
                .attr("stroke-width", 4)
                .attr("stroke-opacity", 0.6)
            })
            .on("mouseout", function() {
              d3.select(this)
                .attr("stroke-width", isTagFiltered ? 3 : 2)
                .attr("stroke-opacity", isTagFiltered ? 0.8 : 0.25)
            })
          

        }
      })
    }

    // Add data points (album covers)
    const imageSize = 36
    const selectedImageSize = 48

    // 获取tag筛选中涉及的所有track hashes
    const tagFilteredTrackHashes = new Set<string>()
    highlightedMixPairIndices.forEach(index => {
      const pair = data.mix_pairs?.find(p => p.index === index)
      if (pair) {
        tagFilteredTrackHashes.add(pair.track_prev)
        tagFilteredTrackHashes.add(pair.track_next)
      }
    })

    g.selectAll(".album-cover")
      .data(data.track_list)
      .enter()
      .append("g")
      .attr("class", d => {
        const isTagFiltered = tagFilteredTrackHashes.has(d.hash_320)
        return `album-cover ${isTagFiltered ? 'tag-filtered' : ''}`
      })
      .attr("data-track-hash", d => d.hash_320)
      .attr("transform", d => {
        // 使用调整后的位置以避免碰撞
        const pos = adjustedPositions.get(d.hash_320) || getTrackPosition(d)
        return `translate(${pos.x}, ${pos.y})`
      })
      .each(function(d) {
        const group = d3.select(this)
        const isSelected = selectedPoint?.hash_320 === d.hash_320
        const isTagFiltered = tagFilteredTrackHashes.has(d.hash_320)
        const size = isSelected ? selectedImageSize : (isTagFiltered ? imageSize * 1.2 : imageSize)

        // Add selection border if selected
        if (isSelected) {
          group.append("rect")
            .attr("class", "selection-border")
            .attr("x", -size/2 - 3)
            .attr("y", -size/2 - 3)
            .attr("width", size + 6)
            .attr("height", size + 6)
            .attr("fill", "none")
            .attr("stroke", "#3b82f6")
            .attr("stroke-width", 3)
            .style("filter", "drop-shadow(0 0 8px rgba(59, 130, 246, 0.6))")
        }

        // Add tag filter border for tag filtered state
        group.append("rect")
          .attr("class", "tag-filter-border")
          .attr("x", -selectedImageSize/2 - 3)
          .attr("y", -selectedImageSize/2 - 3)
          .attr("width", selectedImageSize + 6)
          .attr("height", selectedImageSize + 6)
          .attr("fill", "none")
          .attr("stroke", "#ffc107")
          .attr("stroke-width", 3)
          .style("filter", "drop-shadow(0 0 8px rgba(255, 193, 7, 0.6))")
          .style("display", isTagFiltered && !isSelected ? "block" : "none")

        // Add highlight border for highlighted state (will be added dynamically)
        group.append("rect")
          .attr("class", "highlight-border")
          .attr("x", -selectedImageSize/2 - 3)
          .attr("y", -selectedImageSize/2 - 3)
          .attr("width", selectedImageSize + 6)
          .attr("height", selectedImageSize + 6)
          .attr("fill", "none")
          .attr("stroke", "#3b82f6")
          .attr("stroke-width", 3)
          .style("filter", "drop-shadow(0 0 8px rgba(59, 130, 246, 0.6))")
          .style("display", "none")

        // Add connected border for connected state (will be added dynamically)
        group.append("rect")
          .attr("class", "connected-border")
          .attr("x", -selectedImageSize/2 - 3)
          .attr("y", -selectedImageSize/2 - 3)
          .attr("width", selectedImageSize + 6)
          .attr("height", selectedImageSize + 6)
          .attr("fill", "none")
          .attr("stroke", "#22c55e")
          .attr("stroke-width", 3)
          .style("filter", "drop-shadow(0 0 8px rgba(34, 197, 94, 0.6))")
          .style("display", "none")

        // Add album cover image
        group.append("image")
          .attr("x", -size/2)
          .attr("y", -size/2)
          .attr("width", size)
          .attr("height", size)
          .attr("href", getVisualizerAssetUrl(`album_covers_normalized/${d.img_fname}`))
          .style("cursor", "pointer")
          .style("filter", isSelected 
            ? "drop-shadow(0 4px 8px rgba(59, 130, 246, 0.5)) brightness(1.1)"
            : isTagFiltered
              ? "drop-shadow(0 3px 6px rgba(255, 193, 7, 0.5)) brightness(1.1)"
              : "drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))")
          .on("click", function(event, clickedTrack) {
            event.stopPropagation()
            const track = clickedTrack as Track
            // 查找该track作为prev的pair
            const pair = data.mix_pairs?.find(p => p.track_prev === track.hash_320)
            if (pair) {
              const nextTrack = data.track_list.find(t => t.hash_320 === pair.track_next)
              if (nextTrack) {
                setSelectedPair({ 
                  prev: track, 
                  next: nextTrack,
                  mixPairIndex: pair.index
                })
                setIsPlayerOpen(true)
              }
            } else {
              // 如果该track不是prev，查找它作为next的情况
              const reversePair = data.mix_pairs?.find(p => p.track_next === track.hash_320)
              if (reversePair) {
                const prevTrack = data.track_list.find(t => t.hash_320 === reversePair.track_prev)
                if (prevTrack) {
                  setSelectedPair({ 
                    prev: prevTrack, 
                    next: track,
                    mixPairIndex: reversePair.index
                  })
                  setIsPlayerOpen(true)
                }
              }
            }
          })
          .on("mouseover", function(_event, hoveredTrack) {
            // Clear any pending timeout
            if (hoverTimeoutRef.current) {
              window.clearTimeout(hoverTimeoutRef.current)
              hoverTimeoutRef.current = null
            }
            const track = hoveredTrack as Track
            setSelectedPoint(track)
            
            // Bring to front by re-appending the parent group
            const parentGroup = d3.select(this.parentNode as SVGGElement)
            parentGroup.raise()
            
            // Don't call highlightConnections here, let useEffect handle it
            
            // Scroll to the corresponding item in both lists (from plot, no exclusion)
            scrollToTrackInList(track.hash_320)
            // Also scroll to connected tracks in next list
            const connectedTracks = getConnectedTracks(track.hash_320)
            connectedTracks.forEach(connectedHash => {
              scrollToTrackInList(connectedHash)
            })
          })
          .on("mouseout", function() {
            // Add delay before clearing selection to avoid flickering
            hoverTimeoutRef.current = window.setTimeout(() => {
              setSelectedPoint(null)
              // Don't call clearHighlights here, let useEffect handle it
              }, 150)
          })
          .on("error", function() {
            // Fallback to colored rectangle
            d3.select(this).remove()
            group.append("rect")
              .attr("x", -size/2)
              .attr("y", -size/2)
              .attr("width", size)
              .attr("height", size)
              .attr("fill", isSelected ? "#3b82f6" : "#8b5cf6")
              .attr("stroke", isSelected ? "#1d4ed8" : "#7c3aed")
              .attr("stroke-width", isSelected ? 2 : 1)
              .style("cursor", "pointer")
              .on("click", function(event, clickedTrack) {
                event.stopPropagation()
                const track = clickedTrack as Track
                // 查找该track作为prev的pair
                const pair = data.mix_pairs?.find(p => p.track_prev === track.hash_320)
                if (pair) {
                  const nextTrack = data.track_list.find(t => t.hash_320 === pair.track_next)
                  if (nextTrack) {
                    setSelectedPair({ prev: track, next: nextTrack })
                    setIsPlayerOpen(true)
                  }
                } else {
                  // 如果该track不是prev，查找它作为next的情况
                  const reversePair = data.mix_pairs?.find(p => p.track_next === track.hash_320)
                  if (reversePair) {
                    const prevTrack = data.track_list.find(t => t.hash_320 === reversePair.track_prev)
                    if (prevTrack) {
                      setSelectedPair({ prev: prevTrack, next: track })
                      setIsPlayerOpen(true)
                    }
                  }
                }
              })
              .on("mouseover", function(_event, hoveredTrack) {
                // Clear any pending timeout
                if (hoverTimeoutRef.current) {
                  window.clearTimeout(hoverTimeoutRef.current)
                  hoverTimeoutRef.current = null
                }
                const track = hoveredTrack as Track
                setSelectedPoint(track)
                
                // Bring to front by re-appending the parent group
                const parentGroup = d3.select(this.parentNode as SVGGElement)
                parentGroup.raise()
                
                // Don't call highlightConnections here, let useEffect handle it
                
                // Scroll to the corresponding item in both lists (from plot, no exclusion)
                scrollToTrackInList(track.hash_320)
                // Also scroll to connected tracks in next list
                const connectedTracks = getConnectedTracks(track.hash_320)
                connectedTracks.forEach(connectedHash => {
                  scrollToTrackInList(connectedHash)
                })
              })
              .on("mouseout", function() {
                // Add delay before clearing selection to avoid flickering
                hoverTimeoutRef.current = window.setTimeout(() => {
                  setSelectedPoint(null)
                  // Don't call clearHighlights here, let useEffect handle it
                }, 150)
              })
          })

        // Add tooltip
        group.append("title")
          .text(`${d.singer_name} - ${d.songname}\n年份: ${d.publish_year}\n流派: ${d.genre_top1}`)
      })

    // 渲染完成后延迟一小段时间再隐藏加载状态，确保所有图片加载完成
    if (!hasRenderedOnce.current) {
      setTimeout(() => {
        setIsRendering(false)
        hasRenderedOnce.current = true // 标记已完成首次渲染
      }, 500)
    }

  }, [data, selectedPoint, selectedTags, tagData, getFilteredMixPairIndices, scrollToTrackInList, getConnectedTracks])

  // Effect to manage highlights based on selectedPoint and Alt key state
  useEffect(() => {
    if (selectedPoint) {
      // Apply highlights when there's a selected point, using reverse mode if Alt is pressed
      highlightConnections(selectedPoint.hash_320, isAltKeyPressed)
    } else {
      // Clear highlights when no point is selected
      clearHighlights()
    }
  }, [selectedPoint, isAltKeyPressed, highlightConnections, clearHighlights])

  // Effect to handle Alt key press/release
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Alt' && !isAltKeyPressed) {
        setIsAltKeyPressed(true)
      }
    }

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Alt' && isAltKeyPressed) {
        setIsAltKeyPressed(false)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('keyup', handleKeyUp)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('keyup', handleKeyUp)
    }
  }, [isAltKeyPressed])

  // Cleanup function
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        window.clearTimeout(hoverTimeoutRef.current)
      }
    }
  }, [])

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex justify-center items-center py-32">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
          <span className="ml-2 text-gray-600 dark:text-gray-400">加载可视化数据...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-8 text-center">
          <div className="text-red-600 dark:text-red-400 mb-2">加载失败</div>
          <div className="text-red-500 dark:text-red-300 text-sm">{error}</div>
        </div>
      </div>
    )
  }

  if (!data) return null

  // Tutorial steps
  const tutorialSteps: TutorialStep[] = [
    {
      title: '欢迎体验 Automix 🎵',
      description: '图表展示了不同年代和流派的音乐连接。让我们快速了解如何交互！',
      position: 'center'
    },
    {
      title: '图表交互：悬停与点击 ✨',
      description: '将鼠标悬停在专辑封面上可以看到连接关系，蓝色表示当前音轨，绿色表示下一首歌。点击任意封面或连线即可试听混音！',
      targetSelector: '.max-w-full > .flex > .flex-1',
      position: 'right',
      highlightPadding: 12
    },
    {
      title: '列表交互：快速定位 🎼',
      description: '右侧列表同样支持悬停和点击。悬停时自动滚动到关联的音轨，点击即可打开播放器试听。',
      targetSelector: '.w-\\[480px\\]',
      position: 'left',
      highlightPadding: 12
    },
    {
      title: '开始探索！🚀',
      description: '试试在图表和列表中悬停、点击不同的音轨，发现你喜欢的音乐组合吧！',
      position: 'center'
    }
  ]

  return (
    <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-12 relative">
      {/* Tutorial Component */}
      <AppTutorial
        isOpen={isTutorialOpen}
        onClose={() => setIsTutorialOpen(false)}
        steps={tutorialSteps}
      />

      {/* QR Code - Top Right Corner */}
      <div className="absolute top-6 right-8 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6 flex flex-col items-center gap-4 z-10">
        <img 
          src={getVisualizerAssetUrl('playlistQRCode.png')} 
          alt="Playlist QR Code" 
          className="w-44 h-44 rounded-lg"
        />
        <div className="text-center">
          <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed max-w-[180px]">
            微信扫码/酷狗音乐扫码或搜索 <span className="font-semibold text-primary-600 dark:text-primary-400">5221249</span> 端上体验歌单
          </p>
        </div>
      </div>

      {/* Header */}
      <div className="text-center mb-8">
        <div className="flex items-center justify-center gap-4 mb-4">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white">
            Automix
            <span className="bg-gradient-to-r from-primary-500 to-blue-600 bg-clip-text text-transparent">
              {' '}Genre/Year Navigator
            </span>
          </h1>
          <button
            onClick={() => setIsTutorialOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-all shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50"
            title="查看使用指南"
          >
            <HelpCircle className="h-5 w-5" />
            <span className="text-sm">如何体验</span>
          </button>
        </div>
        <p className="text-xl text-gray-600 dark:text-gray-400 mb-6 max-w-3xl mx-auto">
          Experience how Automix connects music from different decades and genres.
        </p>
        
        {/* Stats */}
        <div className="flex justify-center space-x-8 mb-6">
          <div className="flex items-center space-x-2 text-gray-600 dark:text-gray-400">
            <Music className="h-5 w-5" />
            <span>Tracks: {data.track_list.length}</span>
          </div>
          <div className="flex items-center space-x-2 text-gray-600 dark:text-gray-400">
            <Calendar className="h-5 w-5" />
            <span>Decade Span: 1980 - 2030</span>
          </div>
          <div className="flex items-center space-x-2 text-gray-600 dark:text-gray-400">
            <Tags className="h-5 w-5" />
            <span>Genres: {data.genres.length}</span>
          </div>
          <div className="flex items-center space-x-2 text-gray-600 dark:text-gray-400">
            <div className="flex items-center space-x-1">
              <div className="w-4 h-0.5 bg-purple-500"></div>
              <div className="w-4 h-0.5 bg-green-500"></div>
            </div>
            <span>Mix Pairs: {data.mix_pairs?.length || 0}</span>
          </div>
        </div>

        {/* Tag Filter Panel */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 max-w-5xl mx-auto">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Filter className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                标签筛选
              </h3>
              {selectedTags.length > 0 && (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  ({getFilteredMixPairIndices().length} mix pairs)
                </span>
              )}
            </div>
            {selectedTags.length > 0 && (
              <button
                onClick={() => setSelectedTags([])}
                className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
              >
                清除筛选
              </button>
            )}
          </div>

          {/* Tag List */}
          {tagData && tagData.allTags.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {tagData.allTags.map((tag) => {
                const color = getTagColor(tag)
                const isSelected = selectedTags.includes(tag)
                const mixPairCount = Object.entries(tagData.mixPairTags).filter(([_, tags]) => 
                  tags.includes(tag)
                ).length

                return (
                  <button
                    key={tag}
                    onClick={() => toggleTagSelection(tag)}
                    className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                      isSelected
                        ? `${color.bg} ${color.text} border-2 ${color.border} shadow-sm`
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    {tag}
                    <span className="text-xs opacity-70">
                      ({mixPairCount})
                    </span>
                  </button>
                )
              })}
            </div>
          ) : (
            <div className="text-center text-sm text-gray-500 dark:text-gray-400 py-2">
              暂无标签，在播放器中为 mix pair 添加标签
            </div>
          )}

          {/* Filtered Mix Pairs */}
          {selectedTags.length > 0 && getFilteredMixPairIndices().length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <h4 className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                匹配的 Mix Pairs:
              </h4>
              <div className="flex flex-wrap gap-2">
                {getFilteredMixPairIndices().map((index) => {
                  const pair = data.mix_pairs?.find(p => p.index === index)
                  if (!pair) return null
                  
                  const prevTrack = data.track_list.find(t => t.hash_320 === pair.track_prev)
                  const nextTrack = data.track_list.find(t => t.hash_320 === pair.track_next)
                  
                  if (!prevTrack || !nextTrack) return null
                  
                  return (
                    <button
                      key={index}
                      onClick={() => openPlayerByTag(index)}
                      className="inline-flex items-center gap-2 px-3 py-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors"
                      title={`${prevTrack.singer_name} - ${prevTrack.songname} → ${nextTrack.singer_name} - ${nextTrack.songname}`}
                    >
                      <span className="text-xs font-mono text-amber-700 dark:text-amber-300">
                        #{index}
                      </span>
                      <span className="text-xs text-gray-600 dark:text-gray-400">
                        {prevTrack.singer_name} → {nextTrack.singer_name}
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      </div>



      {/* Main Content with Side Panel */}
      <div className="flex gap-6">
        {/* Main Visualization */}
        <div className="flex-1 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="relative w-full overflow-auto" style={{ height: '1040px' }}>
            {/* 加载状态遮罩 */}
            {isRendering && (
              <div className="absolute inset-0 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm z-10 flex items-center justify-center">
                <div className="flex flex-col items-center gap-4">
                  <Loader2 className="h-12 w-12 animate-spin text-primary-600" />
                  <div className="text-gray-900 dark:text-white font-medium">渲染可视化图表...</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    正在加载 {data?.track_list.length} 个音轨和 {data?.mix_pairs?.length || 0} 个连接
                  </div>
                </div>
              </div>
            )}
            
            <svg
              ref={svgRef}
              width={1800}
              height={1000}
              className="block mx-auto"
              style={{ minWidth: 1800, minHeight: 1000 }}
            />
          </div>
        </div>

        {/* Side Panel for Track Lists */}
        <div className="w-[480px] flex-shrink-0">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 sticky top-6 h-[1040px] flex flex-col">
            {/* Headers */}
            <div className="flex border-b border-gray-200 dark:border-gray-600">
              <div className="flex-1 px-4 py-3 text-center">
                <h3 className="text-sm font-medium text-gray-900 dark:text-white">Track Prev</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">Starting tracks</p>
              </div>
              <div className="w-px bg-gray-200 dark:bg-gray-600"></div>
              <div className="flex-1 px-4 py-3 text-center">
                <h3 className="text-sm font-medium text-gray-900 dark:text-white">Track Next</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">Following tracks</p>
              </div>
            </div>
            
            {/* Lists Container */}
            <div className="flex-1 flex overflow-hidden">
              {/* Track Prev List */}
              <div className="flex-1 overflow-y-auto scrollbar-thin" ref={prevListContainerRef}>
                <div className="p-4 space-y-2">
                  {(() => {
                    // Get tracks that appear as track_prev
                    const prevTracks = data.mix_pairs?.map(pair => pair.track_prev) || []
                    const prevTrackList = data.track_list.filter(track => 
                      prevTracks.includes(track.hash_320)
                    )
                    
                    return prevTrackList.map((track) => {
                      const isSelected = selectedPoint?.hash_320 === track.hash_320
                      return (
                        <div
                          key={track.hash_320}
                          data-track-hash={track.hash_320}
                          className={`p-3 rounded-lg border transition-all duration-200 cursor-pointer ${
                            isSelected
                              ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700 shadow-sm'
                              : 'bg-gray-50 dark:bg-gray-700/50 border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700'
                          }`}
                          onClick={() => {
                            setSelectedPoint(track)
                            // 查找该track作为prev的pair
                            const pair = data.mix_pairs?.find(p => p.track_prev === track.hash_320)
                            if (pair) {
                              const nextTrack = data.track_list.find(t => t.hash_320 === pair.track_next)
                              if (nextTrack) {
                                setSelectedPair({ 
                                  prev: track, 
                                  next: nextTrack,
                                  mixPairIndex: pair.index 
                                })
                                setIsPlayerOpen(true)
                              }
                            }
                          }}
                          onMouseEnter={() => {
                            if (hoverTimeoutRef.current) {
                              window.clearTimeout(hoverTimeoutRef.current)
                              hoverTimeoutRef.current = null
                            }
                            setSelectedPoint(track)
                            // Scroll to connected tracks in next list only (exclude current prev list)
                            const connectedTracks = getConnectedTracks(track.hash_320)
                            connectedTracks.forEach(connectedHash => {
                              scrollToTrackInList(connectedHash, 'prev')
                            })
                          }}
                          onMouseLeave={() => {
                            hoverTimeoutRef.current = window.setTimeout(() => {
                              setSelectedPoint(null)
                            }, 150)
                          }}
                        >
                          <div className="flex items-center space-x-3">
                            {/* Album Cover */}
                            <div className="flex-shrink-0">
                              <img
                                src={getVisualizerAssetUrl(`album_covers_normalized/${track.img_fname}`)}
                                alt={`${track.singer_name} - ${track.songname}`}
                                className="w-10 h-10 rounded-md object-cover shadow-sm"
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement
                                  target.style.display = 'none'
                                  const fallback = target.nextElementSibling as HTMLElement
                                  if (fallback) fallback.style.display = 'flex'
                                }}
                              />
                              <div className="w-10 h-10 bg-gray-200 dark:bg-gray-600 rounded-md hidden items-center justify-center">
                                <Music className="h-4 w-4 text-gray-400" />
                              </div>
                            </div>
                            
                            {/* Track Info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between mb-1">
                                <h4 className={`text-xs font-medium truncate ${
                                  isSelected ? 'text-blue-900 dark:text-blue-100' : 'text-gray-900 dark:text-white'
                                }`}>
                                  {track.singer_name}
                                </h4>
                                <span className={`text-xs px-1.5 py-0.5 rounded text-xs ${
                                  isSelected 
                                    ? 'bg-blue-100 dark:bg-blue-800 text-blue-700 dark:text-blue-200' 
                                    : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300'
                                }`}>
                                  {track.publish_year}
                                </span>
                              </div>
                              
                              <p className={`text-xs truncate mb-1 ${
                                isSelected ? 'text-blue-700 dark:text-blue-300' : 'text-gray-600 dark:text-gray-400'
                              }`}>
                                {track.songname}
                              </p>
                              
                              <p className={`text-xs ${
                                isSelected ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-500'
                              }`}>
                                {track.genre_top1}
                              </p>
                            </div>
                          </div>
                        </div>
                      )
                    })
                  })()}
                </div>
              </div>
              
              {/* Divider */}
              <div className="w-px bg-gray-200 dark:bg-gray-600"></div>
              
              {/* Track Next List */}
              <div className="flex-1 overflow-y-auto scrollbar-thin" ref={nextListContainerRef}>
                <div className="p-4 space-y-2">
                  {(() => {
                    // Get tracks that appear as track_next
                    const nextTracks = data.mix_pairs?.map(pair => pair.track_next) || []
                    const nextTrackList = data.track_list.filter(track => 
                      nextTracks.includes(track.hash_320)
                    )
                    
                    return nextTrackList.map((track) => {
                      const isConnectedNext = selectedPoint ? getConnectedTracks(selectedPoint.hash_320).includes(track.hash_320) : false
                      return (
                        <div
                          key={track.hash_320}
                          data-track-hash={track.hash_320}
                          className={`p-3 rounded-lg border transition-all duration-200 cursor-pointer ${
                            isConnectedNext
                              ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700 shadow-sm'
                              : 'bg-gray-50 dark:bg-gray-700/50 border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700'
                          }`}
                          onClick={() => {
                            // Find the pair that connects to this track_next
                            const pair = data.mix_pairs?.find(p => p.track_next === track.hash_320)
                            if (pair) {
                              const prevTrackObj = data.track_list.find(t => t.hash_320 === pair.track_prev)
                              if (prevTrackObj) {
                                setSelectedPoint(prevTrackObj)
                                setSelectedPair({ 
                                  prev: prevTrackObj, 
                                  next: track,
                                  mixPairIndex: pair.index
                                })
                                setIsPlayerOpen(true)
                              }
                            }
                          }}
                          onMouseEnter={() => {
                            if (hoverTimeoutRef.current) {
                              window.clearTimeout(hoverTimeoutRef.current)
                              hoverTimeoutRef.current = null
                            }
                            // Find the track_prev that connects to this track_next
                            const prevTrack = data.mix_pairs?.find(pair => pair.track_next === track.hash_320)?.track_prev
                            const prevTrackObj = prevTrack ? data.track_list.find(t => t.hash_320 === prevTrack) : null
                            setSelectedPoint(prevTrackObj || track)
                            // Scroll to the prev track in prev list only (exclude current next list)
                            if (prevTrack) {
                              scrollToTrackInList(prevTrack, 'next')
                            }
                          }}
                          onMouseLeave={() => {
                            hoverTimeoutRef.current = window.setTimeout(() => {
                              setSelectedPoint(null)
                            }, 150)
                          }}
                        >
                          <div className="flex items-center space-x-3">
                            {/* Album Cover */}
                            <div className="flex-shrink-0">
                              <img
                                src={getVisualizerAssetUrl(`album_covers_normalized/${track.img_fname}`)}
                                alt={`${track.singer_name} - ${track.songname}`}
                                className="w-10 h-10 rounded-md object-cover shadow-sm"
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement
                                  target.style.display = 'none'
                                  const fallback = target.nextElementSibling as HTMLElement
                                  if (fallback) fallback.style.display = 'flex'
                                }}
                              />
                              <div className="w-10 h-10 bg-gray-200 dark:bg-gray-600 rounded-md hidden items-center justify-center">
                                <Music className="h-4 w-4 text-gray-400" />
                              </div>
                            </div>
                            
                            {/* Track Info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between mb-1">
                                <h4 className={`text-xs font-medium truncate ${
                                  isConnectedNext ? 'text-green-900 dark:text-green-100' : 'text-gray-900 dark:text-white'
                                }`}>
                                  {track.singer_name}
                                </h4>
                                <span className={`text-xs px-1.5 py-0.5 rounded text-xs ${
                                  isConnectedNext
                                    ? 'bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-200'
                                    : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300'
                                }`}>
                                  {track.publish_year}
                                </span>
                              </div>
                              
                              <p className={`text-xs truncate mb-1 ${
                                isConnectedNext ? 'text-green-700 dark:text-green-300' : 'text-gray-600 dark:text-gray-400'
                              }`}>
                                {track.songname}
                              </p>
                              
                              <p className={`text-xs ${
                                isConnectedNext ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-gray-500'
                              }`}>
                                {track.genre_top1}
                              </p>
                            </div>
                          </div>
                        </div>
                      )
                    })
                  })()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 悬浮播放器 */}
      {selectedPair && (() => {
        // 获取 mixPairData
        let mixPairData: any = undefined
        let sequenceId: string | undefined = undefined
        
        if (mixinfo && selectedPair.mixPairIndex !== undefined) {
          const mixPair = mixinfo.mix_pairs[selectedPair.mixPairIndex]
          if (mixPair) {
            mixPairData = mixPair
            sequenceId = mixinfo.sequence_id
            console.log('🎵 Loading mix pair', mixPair.index)
          }
        }
        
        return (
          <FloatingMusicPlayer
            prevTrack={{
              name: selectedPair.prev.songname,
              artist: selectedPair.prev.singer_name,
              albumCover: getVisualizerAssetUrl(`album_covers_normalized/${selectedPair.prev.img_fname}`),
              bpm: mixPairData?.track_prev.bpm,
              genre: selectedPair.prev.genre_top1,
              year: selectedPair.prev.publish_year,
            }}
            nextTrack={{
              name: selectedPair.next.songname,
              artist: selectedPair.next.singer_name,
              albumCover: getVisualizerAssetUrl(`album_covers_normalized/${selectedPair.next.img_fname}`),
              bpm: mixPairData?.track_next.bpm,
              genre: selectedPair.next.genre_top1,
              year: selectedPair.next.publish_year,
            }}
            mixPairData={mixPairData}
            sequenceId={sequenceId}
            isOpen={isPlayerOpen}
            onClose={() => {
              setIsPlayerOpen(false)
              // 关闭播放器时刷新tags
              refreshTags()
            }}
          />
        )
      })()}
    </div>
  )
}
